<template>
  <VSpinner v-if="!product" class="offset-top-16 offset-bottom-17" />
  <article v-else>
    <div id="PRODUCT_ORDER_CONFIRMATION" class="container-fixed">
      <!-- #53 -->
      <VProgress
        v-if="flags.progressBarEnabled"
        class="offset-top-2 offset-bottom-2"
        :steps="steps"
        :active="'substore.' + (isOptin ? 'optin_' : '') + 'progress_confirmation'"
      />

      <VSection cluster :label="isConfirmationInProgress ? 'substore.confirmation_in_progress' : 'substore.order_completed'">
        <p v-html="product.texts.productConfirmation"><!-- #95 --></p>
        <p v-html="product.texts.productConfirmationBeforeButtons"><!-- #99 --></p>

        <!-- #100 -->
        <p>
          <span v-for="(b, index) in product.productDescription.buttons" :key="index" class="offset-right-1">
            <a v-if="b.resourcePath" :href="b.url" class="center" target="_blank"><img :src="`delegates/product/content/${b.resourcePath}`" :alt="b.alt"/></a>
            <a v-else :href="b.url" class="btn btn-primary btn-brand center" target="_blank">{{ b.alt }}</a>
          </span>
        </p>

        <p v-html="product.texts.productConfirmationAfterButtons"><!-- #101 --></p>
      </VSection>

      <!-- #102 -->
      <section class="offset-top-2 pull-right offset-right-1 button-bar">
        <router-link v-if="nextButtonType === PRODUCT_CATALOGUE" class="btn btn-default btn-brand" :to="{ name: PRODUCT_CATALOGUE }">
          {{ $t('substore.orderconfirmation_btn_next') }}
        </router-link>
        <router-link v-else-if="nextButtonType === STOCK" class="btn btn-default btn-brand" :to="{ name: STOCK }">
          {{ $t('common.navigation_STOCK') }}
        </router-link>
      </section>
    </div>

    <div class="container-fixed offset-top-6"> <VFootnotes :footnotes="footnotes" /><!-- #166 --> </div>
  </article>
</template>

<script>
import { mapGetters } from 'vuex';
import Proxy from '@/proxies/OrderConfirmationProxy';
import NormalTransformer from '@/transformers/ProductOrderConfirmationTransformer';
import OptinTransformer from '@/transformers/ProductOrderConfirmationOptinTransformer';
import InProgressTransformer from '@/transformers/ProductOrderConfirmationInProgressTransformer';
import Footnotes from '@/utils/Footnotes';
import TitleMixin from '@/mixins/title';
import OrderStepsMixin from '@/mixins/orderSteps';
import VSpinner from '@/components/Spinner';
import VSection from '@/components/Section';
import emitter from '@/plugins/emitter';
import { PRODUCT_CATALOGUE, PRODUCT_ORDER_CONFIRMATION_FINALIZE, PRODUCT_ORDER_CONFIRMATION_OPTIN, STOCK } from '@route-types';

export default {
  name: 'ProductConfirmation',

  components: {
    VSpinner,
    VSection,
    VProgress: () => import('@/components/Progress'),
    VFootnotes: () => import('@/components/UI/Footnotes'),
  },

  mixins: [TitleMixin, OrderStepsMixin],

  props: {
    subscriptionTemplateId: {
      type: String,
      required: true,
    },

    confirmationInProgress: {
      type: String,
      required: false,
      default: null,
    },

    thirdPartyAuthentication: {
      type: Boolean,
      required: false,
    },

    priceEncoded: {
      type: String,
      required: false,
      default: null,
    },
  },

  data() {
    return {
      PRODUCT_CATALOGUE,
      STOCK,

      isOptin: this.$route.name === PRODUCT_ORDER_CONFIRMATION_OPTIN,
      isSubscriptionBookedByExtern: this.$route.name === PRODUCT_ORDER_CONFIRMATION_FINALIZE,
      product: null,
      footnotes: null,
      flags: {
        progressBarEnabled: null,
        orderSelectionSkipped: null,
        thirdPartyAuthentication: this.thirdPartyAuthentication,
      },
    };
  },

  computed: {
    ...mapGetters('system', ['hasAccessCatalogue', 'hasAccessStockOverview']),

    nextButtonType() {
      if (this.hasAccessStockOverview && (this.isOptin || this.isSubscriptionBookedByExtern)) {
        return STOCK;
      } else if (this.hasAccessCatalogue && (!this.flags.subscriptionBookedByExtern /* DescElemToBeSetToFinalize */ || !this.hasAccessStockOverview)) {
        return PRODUCT_CATALOGUE;
      } else if (this.hasAccessStockOverview) {
        return STOCK;
      }
      return null;
    },

    isConfirmationInProgress() {
      return !!this.confirmationInProgress;
    },
  },

  async created() {
    const response = await new Proxy({
      optin: this.isOptin,
      confirmationInProgress: this.isConfirmationInProgress,
    }).find(this.subscriptionTemplateId);

    if (response.head.fault !== null) throw new Error(response.head.fault);

    Object.keys(this.flags).forEach(key => {
      this.flags[key] = response.data[key];
    });

    this.footnotes = new Footnotes();
    const Transformer = this.getTransformer();
    this.product = Transformer.fetch(response.data.product, this.footnotes);
    emitter.emit('setBannerPicture', this.product.images.headerImageConfirmationResourcePath);
    emitter.emit('setBannerHeadline', this.product.texts.headerTitleConfirmation);
    emitter.emit('setBannerSubheadline', this.product.texts.headerSubTitleConfirmation);

    let pageContentSuffix = 'bestellbestaetigung';
    if (this.isOptin) pageContentSuffix += 'optin';
    else if (this.isConfirmationInProgress) pageContentSuffix += 'inbearbeitung';
    this.$webtrekk.viewPageWithPayload(pageContentSuffix, {
      page_type: 'basket',
      shop_product_sku: this.subscriptionTemplateId,
      shop_product_status: 'conf',
      shop_product_name: this.product.texts.productName,
      shop_product_quantity: 1,
      shop_order_value: this.priceEncoded ? atob(this.priceEncoded) : 0,
    });
  },

  methods: {
    getTransformer() {
      if (this.isOptin) {
        return OptinTransformer;
      } else if (this.isConfirmationInProgress) {
        return InProgressTransformer;
      }
      return NormalTransformer;
    },
  },
};
</script>

<style scoped>
.button-bar > span {
  position: relative;
  top: 1px;
  display: inline-block;
}
.center {
  position: relative;
  vertical-align: top;
}
</style>
